import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  fetchGift,
  sendGifts,
  fetchGiftPlans,
  subscribeFromGift,
  confirmGiftCheckout,
  GiftContext,
  GiftPlan,
  PostcardTheme,
} from "./api";

/**
 * Personalised business gifting page (Outreach spec §7).
 *
 * The whole point of this screen is that it asks for ONE thing: recipient email
 * addresses (the greeting is pre-filled, editable if they want). No account, no
 * plan selection, no payment details, no credit card — the firm should be
 * finished in 15–30 seconds. It continues the branding established in the
 * invitation email so it never feels like a different site.
 */
export default function GiftPage({ token }: { token: string }) {
  const [gift, setGift] = useState<GiftContext | null>(null);
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<string[]>(["", "", ""]);
  // The firm's own greeting to its recipients, pre-filled with the default.
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sentCount, setSentCount] = useState<number | null>(null);
  const [plans, setPlans] = useState<GiftPlan[]>([]);
  const [subscribing, setSubscribing] = useState<string | null>(null);
  const [paid, setPaid] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    let alive = true;
    (async () => {
      // Returning from Stripe: confirm the payment before painting the page so
      // the new plan is reflected immediately, webhook or not.
      const params = new URLSearchParams(window.location.search);
      if (params.get("checkout") === "success" && params.get("session_id")) {
        const res = await confirmGiftCheckout(token, params.get("session_id")!);
        if (alive && res.success) setPaid(true);
        window.history.replaceState({}, "", window.location.pathname);
      } else if (params.get("checkout") === "cancelled") {
        window.history.replaceState({}, "", window.location.pathname);
      }

      const [data, planData] = await Promise.all([fetchGift(token), fetchGiftPlans(token)]);
      if (!alive) return;
      setGift(data);
      setMessage(data?.default_message ?? "");
      setPlans(planData.plans);
      setLoading(false);
    })();
    return () => {
      alive = false;
    };
  }, [token]);

  const startCheckout = async (planKey: string) => {
    setSubscribing(planKey);
    setError(null);
    const res = await subscribeFromGift(token, planKey);
    if (res.success && res.url) {
      window.location.href = res.url;
      return;
    }
    setSubscribing(null);
    setError(
      res.error === "stripe_not_configured" || res.error === "plan_price_not_configured"
        ? "Subscriptions aren't available right now. Please contact support@winterdesktops.com."
        : "Checkout could not be started. Please try again.",
    );
  };

  const max = gift?.licences_remaining ?? 25;
  const filled = useMemo(() => rows.map((r) => r.trim()).filter(Boolean), [rows]);

  const setRow = useCallback((i: number, value: string) => {
    setRows((prev) => {
      const next = [...prev];
      next[i] = value;
      return next;
    });
  }, []);

  const addRow = () => setRows((prev) => (prev.length >= max ? prev : [...prev, ""]));
  const removeRow = (i: number) =>
    setRows((prev) => (prev.length <= 1 ? [""] : prev.filter((_, idx) => idx !== i)));

  const submit = async () => {
    setError(null);
    if (filled.length === 0) {
      setError("Please enter at least one email address.");
      return;
    }
    if (filled.length > max) {
      setError(`You can send to ${max} more recipient${max === 1 ? "" : "s"}.`);
      return;
    }
    setSending(true);
    const res = await sendGifts(token, filled, message);
    setSending(false);

    if (!res.success) {
      setError(res.message || "Winter Panda could not be sent. Please try again.");
      return;
    }
    setSentCount(res.created ?? filled.length);
    if (res.gift) setGift(res.gift);
    setRows(["", "", ""]);
    if (res.invalid?.length) {
      setError(`Skipped ${res.invalid.length} address that didn't look valid: ${res.invalid.join(", ")}`);
    }
  };

  if (loading) {
    return (
      <Shell>
        <div className="mx-auto h-10 w-10 animate-spin rounded-full border-4 border-white/15 border-t-aurora-cyan" />
      </Shell>
    );
  }

  if (!gift) {
    return (
      <Shell>
        <Card>
          <h1 className="font-display text-3xl font-semibold text-frost-50">Link not valid</h1>
          <p className="mt-4 text-frost-200/80">
            This gifting link doesn&apos;t look right. Please check the link in your email, or
            contact us and we&apos;ll send a fresh one.
          </p>
          <a href="/" className="mt-6 inline-block text-aurora-cyan hover:text-frost-50">
            Visit winterdesktops.com →
          </a>
        </Card>
      </Shell>
    );
  }

  // Just came back from a successful Stripe checkout.
  if (paid) {
    return (
      <Shell>
        <Card>
          <div className="text-5xl">🎉</div>
          <h1 className="mt-4 font-display text-3xl font-semibold text-frost-50">
            Thank you — you&apos;re subscribed
          </h1>
          <p className="mt-4 text-frost-200/85">
            <strong>{gift.company_name}</strong> now has a paid annual Winter Panda subscription.
            Your receipt is on its way from Stripe.
          </p>
          <button
            onClick={() => setPaid(false)}
            className="mt-6 rounded-full bg-gradient-to-br from-cozy-300 via-cozy-400 to-cozy-500 px-8 py-3 font-semibold text-midnight-900 shadow-cozy transition-transform hover:-translate-y-0.5"
          >
            Start sending Winter Panda
          </button>
          <p className="mt-5 text-xs text-frost-200/55">
            To manage your subscription later, use “Forgot password” at the business portal to set a
            password for {gift.company_name}.
          </p>
        </Card>
      </Shell>
    );
  }

  // Successfully sent — confirmation, with the option to send more.
  if (sentCount !== null) {
    return (
      <Shell>
        <Card>
          <div className="text-5xl">🎁</div>
          <h1 className="mt-4 font-display text-3xl font-semibold text-frost-50 sm:text-4xl">
            Winter Panda is on its way
          </h1>
          <p className="mt-4 text-frost-200/85">
            {sentCount} personalised invitation{sentCount === 1 ? "" : "s"} sent, branded for{" "}
            <strong>{gift.company_name}</strong>. Each recipient gets your postcard and their own
            Winter Panda desktop companion.
          </p>
          {error && <p className="mt-4 text-sm text-cozy-300">{error}</p>}
          <div className="mt-6 rounded-2xl glass px-5 py-4 text-sm text-frost-200/80">
            <strong className="text-frost-50">{gift.licences_remaining}</strong> of{" "}
            {gift.licences_total} complimentary licences remaining.
          </div>
          {gift.licences_remaining > 0 && (
            <button
              onClick={() => {
                setSentCount(null);
                setError(null);
              }}
              className="mt-6 rounded-full bg-gradient-to-br from-cozy-300 via-cozy-400 to-cozy-500 px-8 py-3 font-semibold text-midnight-900 shadow-cozy transition-transform hover:-translate-y-0.5"
            >
              Send to more people
            </button>
          )}
        </Card>
      </Shell>
    );
  }

  const expired = !gift.can_gift;

  return (
    <Shell>
      <div className="w-full max-w-2xl">
        {/* The firm's personalised postcard — the same artefact they saw in the
            invitation email, so the experience continues rather than restarts
            (Outreach spec §7 step 1). */}
        <div className="mb-8">
          <Postcard companyName={gift.company_name} logoUrl={gift.logo_url} theme={gift.theme} />
        </div>

        {expired ? (
          <div className="w-full">
            <div className="rounded-[2rem] glass p-8 text-center shadow-glass">
              <h2 className="font-display text-2xl font-semibold text-frost-50">
                This complimentary period has ended
              </h2>
              <p className="mx-auto mt-4 max-w-md text-frost-200/80">
                Colleagues who already installed Winter Panda keep it. To send more, continue with a
                paid annual subscription — no account needed, just checkout.
              </p>
            </div>
            <PlanPicker
              plans={plans}
              busy={subscribing}
              error={error}
              onChoose={startCheckout}
              heading="Continue your subscription"
            />
          </div>
        ) : (
          <>
            <div className="rounded-[2rem] glass p-7 shadow-glass sm:p-9">
              <h2 className="text-center font-display text-2xl font-semibold leading-snug text-frost-50 sm:text-[28px]">
                Gift your firm&apos;s branded Winter Panda in under 30 seconds.
              </h2>
              <p className="mt-3 text-center text-frost-200/80">
                Enter your recipients&apos; email addresses, and personalise your message if you like.
              </p>

              <div className="mt-7 space-y-3">
                {rows.map((value, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <span className="w-6 flex-none text-right text-sm text-frost-200/45">
                      {i + 1}
                    </span>
                    <input
                      type="email"
                      value={value}
                      onChange={(e) => setRow(i, e.target.value)}
                      placeholder={`colleague${i + 1}@example.com`}
                      className="w-full rounded-xl border border-white/12 bg-white/[0.04] px-4 py-3 text-frost-50 outline-none transition-colors placeholder:text-frost-200/35 focus:border-aurora-cyan"
                    />
                    <button
                      type="button"
                      onClick={() => removeRow(i)}
                      aria-label={`Remove recipient ${i + 1}`}
                      className="flex h-9 w-9 flex-none items-center justify-center rounded-lg text-frost-200/50 transition-colors hover:bg-white/10 hover:text-frost-50"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>

              <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
                <button
                  type="button"
                  onClick={addRow}
                  disabled={rows.length >= max}
                  className="text-sm font-semibold text-aurora-cyan transition-colors hover:text-frost-50 disabled:opacity-40"
                >
                  + Add recipient
                </button>
                <span className="text-sm text-frost-200/60">
                  {filled.length} of {max} recipient{max === 1 ? "" : "s"}
                </span>
              </div>

              <div className="mt-7">
                <div className="mb-2 flex items-baseline justify-between gap-3">
                  <label htmlFor="gift-message" className="text-sm font-semibold text-frost-100">
                    Your message to recipients
                  </label>
                  {message !== gift.default_message && (
                    <button
                      type="button"
                      onClick={() => setMessage(gift.default_message)}
                      className="text-xs font-semibold text-aurora-cyan transition-colors hover:text-frost-50"
                    >
                      Use default
                    </button>
                  )}
                </div>
                <textarea
                  id="gift-message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  maxLength={gift.message_max_length}
                  rows={3}
                  className="w-full resize-y rounded-xl border border-white/12 bg-white/[0.04] px-4 py-3 leading-relaxed text-frost-50 outline-none transition-colors placeholder:text-frost-200/35 focus:border-aurora-cyan"
                />
                <div className="mt-1.5 flex justify-between gap-3 text-xs text-frost-200/50">
                  <span>Shown in each recipient&apos;s email, beneath your postcard.</span>
                  <span className="flex-none">
                    {message.length}/{gift.message_max_length}
                  </span>
                </div>
              </div>

              {error && (
                <div className="mt-5 rounded-xl border border-rose-400/25 bg-rose-500/10 px-4 py-3 text-sm text-rose-200">
                  {error}
                </div>
              )}

              <button
                onClick={submit}
                disabled={sending || filled.length === 0}
                className="mt-6 w-full rounded-full bg-gradient-to-br from-cozy-300 via-cozy-400 to-cozy-500 px-8 py-4 text-base font-semibold text-midnight-900 shadow-cozy transition-all hover:-translate-y-0.5 disabled:cursor-default disabled:opacity-50 disabled:hover:translate-y-0"
              >
                {sending ? "Sending…" : "Send Winter Panda Invitations"}
              </button>

              <p className="mt-4 text-center text-xs text-frost-200/55">
                No sign-up, no payment and no credit card required.
              </p>
            </div>

            {/* Upsell to a paid plan for custom templates (spec §5 step 3) */}
            <details className="group mt-5 rounded-2xl glass px-6 py-5">
              <summary className="cursor-pointer list-none text-center text-sm text-frost-200/75 marker:hidden">
                Prefer to choose your own template?{" "}
                <span className="font-semibold text-aurora-cyan group-open:hidden">
                  See paid subscriptions →
                </span>
              </summary>
              <PlanPicker
                plans={plans}
                busy={subscribing}
                error={error}
                onChoose={startCheckout}
                heading=""
              />
            </details>

            {/* What each side receives */}
            <div className="mt-8 grid gap-5 sm:grid-cols-2">
              <Panel title="Your firm receives">
                <li>Complimentary 12-month subscription</li>
                <li>Up to {gift.licences_total} gift recipients</li>
                <li>Future Winter Panda software updates</li>
                <li>Future seasonal postcard collections</li>
                <li>No payment or credit card required</li>
              </Panel>
              <Panel title="Your recipients receive">
                <li>Your firm&apos;s branded digital postcard</li>
                <li>Their own Winter Panda desktop companion</li>
                <li>Nature sounds and gentle daily reflections</li>
                <li>Future software updates</li>
              </Panel>
            </div>
          </>
        )}
      </div>
    </Shell>
  );
}

/**
 * Paid annual plans, chosen straight from the gifting page. Checkout is the only
 * point at which a firm is asked for payment details (spec §5), and it needs no
 * portal account — the gifting token identifies them.
 */
function PlanPicker({
  plans,
  busy,
  error,
  onChoose,
  heading,
}: {
  plans: GiftPlan[];
  busy: string | null;
  error: string | null;
  onChoose: (planKey: string) => void;
  heading: string;
}) {
  if (plans.length === 0) return null;
  return (
    <div className="mt-6">
      {heading && (
        <h3 className="mb-4 text-center font-display text-xl font-semibold text-frost-50">
          {heading}
        </h3>
      )}
      <div className="grid gap-4 sm:grid-cols-3">
        {plans.map((p) => {
          const featured = p.plan_key === "professional";
          return (
            <div
              key={p.plan_key}
              className={`rounded-2xl glass p-5 text-center ${
                featured ? "ring-2 ring-aurora-cyan/40" : ""
              }`}
            >
              <p className="font-display text-lg font-semibold text-frost-50">{p.name}</p>
              <p className="mt-2">
                <span className="font-display text-3xl font-semibold text-frost-50">
                  ${p.price_usd}
                </span>
                <span className="text-sm text-frost-200/60">/year</span>
              </p>
              <p className="mt-1 text-xs text-frost-200/65">{p.licences} licences</p>
              <button
                onClick={() => onChoose(p.plan_key)}
                disabled={busy !== null}
                className={`mt-4 w-full rounded-full px-5 py-2.5 text-sm font-semibold transition-transform disabled:opacity-60 ${
                  featured
                    ? "bg-gradient-to-br from-cozy-300 via-cozy-400 to-cozy-500 text-midnight-900 shadow-cozy hover:-translate-y-0.5"
                    : "text-frost-50 ring-1 ring-white/15 hover:bg-white/10"
                }`}
              >
                {busy === p.plan_key ? "Opening checkout…" : "Continue Subscription"}
              </button>
            </div>
          );
        })}
      </div>
      {error && (
        <div className="mt-4 rounded-xl border border-rose-400/25 bg-rose-500/10 px-4 py-3 text-center text-sm text-rose-200">
          {error}
        </div>
      )}
      <p className="mt-4 text-center text-xs text-frost-200/55">
        Secure payment through Stripe. Your existing recipients keep their Winter Panda.
      </p>
    </div>
  );
}

/** On-page rendering of the branded postcard shown in the invitation email. */
/** Mirrors the emailed postcard, so the firm previews exactly what recipients get. */
function Postcard({
  companyName,
  logoUrl,
  theme,
}: {
  companyName: string;
  logoUrl: string | null;
  theme: PostcardTheme;
}) {
  return (
    <div className="relative mx-auto max-w-md">
      <div className="absolute inset-0 -z-10 rounded-[2.5rem] bg-aurora-band bg-[length:200%_200%] opacity-25 blur-2xl animate-aurora" />
      <div className="rounded-[2rem] glass p-6 text-center shadow-glass">
        {logoUrl && (
          <img
            src={logoUrl}
            alt={companyName}
            className="mx-auto mb-4 max-h-12 max-w-[170px] object-contain"
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.display = "none";
            }}
          />
        )}
        {/* Designed postcards carry their greeting on the artwork itself. */}
        {!theme.designed && (
          <>
            <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-aurora-cyan">
              {theme.motif}
            </p>
            <p className="mt-3 font-display text-xl font-semibold text-frost-50">{theme.heading}</p>
            <p className="mx-auto mt-1.5 max-w-xs text-sm leading-relaxed text-frost-200/75">
              {theme.sentiment}
            </p>
          </>
        )}
        <img
          src={theme.artwork_url}
          alt={theme.designed ? `${theme.heading} — ${theme.sentiment}` : "Winter Panda — a cozy desktop companion"}
          className="mx-auto mt-4 w-full rounded-[1.25rem]"
          onError={(e) => {
            // A template without its own artwork yet falls back to the classic panda.
            const img = e.currentTarget as HTMLImageElement;
            if (!img.src.endsWith("/images/image1.png")) img.src = "/images/image1.png";
          }}
        />
        <div className="mx-auto mt-5 h-px w-40 bg-aurora-cyan/40" />
        <p className="mt-4 font-display text-lg font-semibold tracking-wide text-frost-50">
          WINTER PANDA
        </p>
        <p className="mt-1 text-xs text-frost-200/60">from</p>
        <p className="mt-1 text-sm font-semibold uppercase tracking-wider text-frost-100">
          {companyName}
        </p>
      </div>
    </div>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="relative min-h-screen overflow-hidden bg-night-fade font-sans text-frost-100">
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-24 left-1/3 h-72 w-72 rounded-full bg-aurora-cyan/20 blur-[120px]" />
        <div className="absolute top-10 right-1/4 h-80 w-80 rounded-full bg-aurora-violet/20 blur-[130px]" />
      </div>
      <div className="relative mx-auto flex min-h-screen max-w-3xl flex-col items-center justify-center px-5 py-16">
        {children}
        <footer className="mt-12 text-center text-xs text-frost-200/45">
          Winter Panda by Winter Desktops ·{" "}
          <a href="/#/legal/privacy" className="hover:text-frost-100">
            Privacy
          </a>{" "}
          ·{" "}
          <a href="/#/legal/terms" className="hover:text-frost-100">
            Terms
          </a>
        </footer>
      </div>
    </div>
  );
}

function Card({ children }: { children: React.ReactNode }) {
  return (
    <div className="w-full max-w-xl rounded-[2rem] glass p-9 text-center shadow-glass">{children}</div>
  );
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-2xl glass p-6">
      <h3 className="font-display text-lg font-semibold text-frost-50">{title}</h3>
      <ul className="mt-3 list-disc space-y-2 pl-5 text-sm text-frost-200/75 marker:text-aurora-cyan/60">
        {children}
      </ul>
    </div>
  );
}
